import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { MassCommService, Message, SentMessageStatistics, ClickStatistics } from '../../shared/index';
import { MenuItem } from 'primeng/api';
import { TranslationService } from 'src/app/shared';
import { MassCommProfileSelectorDialogComponent } from './mass-comm-profile-selector-dialog/mass-comm-profile-selector-dialog.component';
import { MassCommMessageEditAnalyticsResendDialogComponent } from './mass-comm-message-edit-analytics-resend-dialog/mass-comm-message-edit-analytics-resend-dialog.component';
import { ConfigService } from 'src/app/config/shared/config.service';

/**
 * message analytics (for not-drafts)
 */
@Component({
  selector: 'wl-mass-comm-message-edit-analytics',
  templateUrl: 'mass-comm-message-edit-analytics.component.html',
  styleUrls: [],
  providers: [],
    standalone: false
})
export class MassCommMessageEditAnalyticsComponent implements OnInit {
  @Input() public message: Message = null;
  @Input() public isDraft: boolean = false;
  @ViewChild(MassCommProfileSelectorDialogComponent) private _profileSelectorDialog: MassCommProfileSelectorDialogComponent;
  @ViewChild(MassCommMessageEditAnalyticsResendDialogComponent)
  _massCommMessageEditAnalyticsResendDialog: MassCommMessageEditAnalyticsResendDialogComponent;

  public loading: boolean = false;
  public loadingStatistics: boolean = false;

  public statistics: SentMessageStatistics = null;

  public clicksTotal: number = 0;
  public clicksUniqueCount: number = 0;

  public openedPercentOfTotal: number = 0;
  public openedPercentOfSucceeded: number = 0;
  public deliveredWidthPercent: number = 0;
  public sendSuccessPercent: number = 0;
  public sendFailPercent: number = 0;
  public clickToReadPercent: number = 0;
  public deliveryDetailMessageId: number = 0;
  public clickDetailMessageId: number = 0;
  public clickDetailUrl: string = '';

  public showDeliveryModal = false;
  public showClickModal = false;
  public menuActionsOptions: MenuItem[];
  private menuResendEmailOptions: MenuItem[];

  public constructor(
    private _massCommService: MassCommService,
    private _translationService: TranslationService
  ) {}

  public ngOnInit(): void {
    this.initMenuResendEmail();
    this.setupComponent();
  }

  private async setupComponent(): Promise<void> {
    if (!this.message || this.message.MessageId <= 0 || this.isDraft) return;

    this.loading = true;

    try {
      this.getStatistics();
    } catch (e) {
    } finally {
      this.loading = false;
    }
  }

  private async getStatistics(): Promise<void> {
    if ((!this.message || this.message.MessageId <= 0 || this.isDraft, this.loadingStatistics)) return;

    this.loadingStatistics = true;

    try {
      this.statistics = await this._massCommService.getMessageStatistics(this.message.MessageId);

      if (!this.statistics) return;

      if (!this.statistics.Clicks) this.statistics.Clicks = [];

      this.clicksTotal = this.statistics.Clicks.map((x) => x.ClickedTotal).reduce((total, x) => total + x, 0);
      this.clicksUniqueCount = this.statistics.Clicks.map((x) => x.UniqueClick).reduce((total, x) => total + x, 0);

      this.openedPercentOfTotal = 0;
      this.openedPercentOfSucceeded = 0;
      this.sendSuccessPercent = 0;
      this.sendFailPercent = 0;
      this.clickToReadPercent = 0;

      if (this.statistics.TotalSent > 0) {
        this.openedPercentOfTotal = Math.min(100, Math.round((this.statistics.UniqueReadCount * 1000) / this.statistics.TotalSent) / 10);
        this.sendSuccessPercent = Math.min(100, Math.floor((this.statistics.SendSuccessCount * 1000) / this.statistics.TotalSent) / 10);
        this.sendFailPercent = Math.min(100, Math.round((this.statistics.SendFailCount * 1000) / this.statistics.TotalSent) / 10);
      }

      if (this.statistics.SendSuccessCount > 0)
        this.openedPercentOfSucceeded = Math.min(
          100,
          Math.round((this.statistics.UniqueReadCount * 1000) / this.statistics.SendSuccessCount) / 10
        );

      if (this.statistics.UniqueReadCount > 0)
        this.clickToReadPercent = Math.min(
          100,
          Math.round((this.statistics.MessageClickUniqueCount * 1000) / this.statistics.UniqueReadCount) / 10
        );

      // two digit numbers
      let roundIf10 = (x: number) => (x > 10 ? Math.round(x) : x);

      this.openedPercentOfSucceeded = roundIf10(this.openedPercentOfSucceeded);
      this.openedPercentOfTotal = roundIf10(this.openedPercentOfTotal);
      this.sendSuccessPercent = roundIf10(this.sendSuccessPercent);
      this.sendFailPercent = roundIf10(this.sendFailPercent);
      this.clickToReadPercent = roundIf10(this.clickToReadPercent);

      for (let click of this.statistics.Clicks) {
        if (this.statistics.UniqueReadCount > 0)
          click.clickPercent = roundIf10(Math.min(100, Math.floor((click.UniqueClick * 1000) / this.statistics.UniqueReadCount) / 10));
      }

      // make sure line graph never exceeds 100%
      if (this.openedPercentOfTotal + this.sendFailPercent > 100) this.openedPercentOfTotal = roundIf10(100 - this.sendFailPercent);

      // just layout
      this.deliveredWidthPercent = 100 - this.openedPercentOfTotal - this.sendFailPercent;
    } catch (e) {
      console.error(e);
    } finally {
      this.loadingStatistics = false;
    }
  }

  public handleRefreshClick() {
    if (this.loadingStatistics) return;

    this.getStatistics();
  }

  public async showReadDetails(): Promise<void> {
    if (!this.message || this.message.MessageId <= 0) return;

    this.showDeliveryModal = true;

    this.deliveryDetailMessageId = this.message.MessageId;
  }

  /**
   * show click details
   * @param click
   */
  public async showClickDetails(click: ClickStatistics): Promise<void> {
    if (!this.message || this.message.MessageId <= 0 || !click || !click.Url) return;

    this.clickDetailMessageId = 0;

    this.showClickModal = true;

    setTimeout(() => {
      this.clickDetailMessageId = this.message.MessageId;
      this.clickDetailUrl = click.Url;
    });
  }

  openProfileSelectorModal() {
    this._profileSelectorDialog.open();
  }

  initMenuResendEmail() {
    this.menuResendEmailOptions = [
      {
        label: this._translationService.getInstantTitlecase(
          'communications.mass-comm-message-edit.analytics.resend.select-button.to-all-recipients'
        ),
        id: 'all',
        command: () => {
          let menuItem = this.menuResendEmailOptions.find((menu) => {
            return menu.id === 'all';
          });

          this._massCommMessageEditAnalyticsResendDialog.open(this.message.MessageId, menuItem);
        },
      },
      {
        label: this._translationService.getInstantTitlecase(
          'communications.mass-comm-message-edit.analytics.resend.select-button.to-unopened-only'
        ),
        id: 'unopened',
        command: () => {
          let menuItem = this.menuResendEmailOptions.find((menu) => {
            return menu.id === 'unopened';
          });

          this._massCommMessageEditAnalyticsResendDialog.open(this.message.MessageId, menuItem);
        },
      },
    ];
  }
}
